<template>
	<div
		ref="liteScroller"
		class="lite-scroller"
		:class="classes"
		@scroll="onScroll"
	>
		<main ref="content" class="lite-scroller__scroll-content">
			<slot></slot>
		</main>
		<footer v-if="isMoreButtonVisible"
			class="more-button-footer"
		>
			<TeamingButton2
				size="TINY"
				type="TERTIARY"
				@click="onScroll"
			>
				<template #left-icon>
					<FontAwesomeIcon :icon="[ `far`, `chevron-double-down` ]" />
				</template>
				<template #default>
					{{ translate( `moreButtonText` ) }}
				</template>
			</TeamingButton2>
		</footer>
	</div>
</template>

<script>
import { debounce } from "@teaming/common-util";
import TranslateMixin from "../mixins/TranslateMixin";
import TeamingButton2 from "./buttons/TeamingButton2.vue";

const scrollers = {};
const pagers = {};

export default {
	name: "LiteScroller",
	teamingPackage: "common-ui",
	mixins: [ TranslateMixin, ],
	emits: [ "scroll", "page", ],
	components: {
		TeamingButton2,
	},
	props: {
		docked: {
			type: Boolean,
			default: true,
		},
		scrollEventDebounce: {
			type: Number,
			default: 100,
		},
		pageEventDebounce: {
			type: Number,
			default: 100,
		},
		pageEventThreshold: {
			type: Number,
			default: 100,
		},
		isPagingEnabled: {
			type: Boolean,
			default: true,
		},
		isPaging: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			liteScrollerId: Symbol(),
			isMoreButtonVisible: false,
			tryToggleMoreButton: null,
		};
	},
	computed: {
		classes() {
			return {
				"lite-scroller--docked": this.docked,
			};
		},
	},
	methods: {
		hasScrollHandle() {
			return !!scrollers[ this.liteScrollerId ];
		},
		hasPagerHandle() {
			return !!pagers[ this.liteScrollerId ];
		},
		clearPageEvent() {
			if ( !this.hasPagerHandle() ) {
				return;
			}
			const { liteScrollerId, } = this;
			clearTimeout( pagers[ liteScrollerId ] );
			pagers[ liteScrollerId ] = null;
			delete pagers[ liteScrollerId ];
		},
		clearScrollEvent() {
			if ( !this.hasScrollHandle() ) {
				return;
			}
			const { liteScrollerId, } = this;
			clearTimeout( scrollers[ liteScrollerId ] );
			scrollers[ liteScrollerId ] = null;
			delete scrollers[ liteScrollerId ];
		},
		schedulePageEvent() {
			const { isPagingEnabled, liteScrollerId, pageEventDebounce, } = this;
			if ( !isPagingEnabled ) {
				return;
			}
			pagers[ liteScrollerId ] = setTimeout( () => {
				// KEEPME
				// console.debug( "paging" );
				this.$emit( "page" );
			}, pageEventDebounce );
		},
		scheduleScrollEvent() {
			const { liteScrollerId, pageEventThreshold, scrollEventDebounce, } = this;
			const { liteScroller, } = this.$refs;
			scrollers[ liteScrollerId ] = setTimeout( () => {
				const {
					clientHeight: displayHeight,
					scrollHeight: contentHeight,
					// distance the top of the *content* is from the top of the scroller
					scrollTop,
				} = liteScroller;

				// distance the bottom of the *content* is from the bottom of the scroller
				const scrollBottom = ( contentHeight - scrollTop - displayHeight );

				// KEEPME
				// console.debug( "scrolling", {
				// 	displayHeight,
				// 	contentHeight,
				// 	scrollTop,
				// 	scrollBottom,
				// } );

				if ( scrollBottom <= pageEventThreshold ) {
					this.schedulePageEvent();
				}

				this.$emit( "scroll" );
			}, scrollEventDebounce );
		},
		onScroll() {
			this.clearScrollEvent();
			this.clearPageEvent();
			this.scheduleScrollEvent();
		},
		toggleMoreButton() {
			const { liteScroller, content, } = this.$refs;
			if ( !( liteScroller && content ) ) {
				return;
			}
			const { isPaging, isPagingEnabled, } = this;
			this.isMoreButtonVisible = isPagingEnabled &&
				!isPaging &&
				( content.clientHeight < liteScroller.clientHeight );
		},
	},
	mounted() {
		this.tryToggleMoreButton = debounce( () => {
			this.toggleMoreButton();
		}, 100 );
		const { screen, } = this.$foundation();
		this.screenWidth = screen.currentWidth();
		screen.on( "resize", this.tryToggleMoreButton );
		this.tryToggleMoreButton();
	},
	updated() {
		this.tryToggleMoreButton();
	},
	beforeUnmount() {
		this.clearScrollEvent();
		this.clearPageEvent();
		const { screen, } = this.$foundation();
		screen.off( "resize", this.tryToggleMoreButton );
		this.tryToggleMoreButton = null;
	},
};
</script>

<style lang="scss" scoped>
.lite-scroller {
	@include auto-y-scroll();

	&--docked {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		width: 100%;
	}

	> .more-button-footer {
		display: flex;
		flex-direction: row;
		justify-content: center;
	}
}
</style>
